/**
 * ScrollTopController - Reactive Controller für Scroll-Top Detection
 * 
 * Verwendet IntersectionObserver, um zu erkennen, ob die Seite am oberen Rand ist.
 * Ähnlich wie React Hooks - kapselt wiederverwendbare Logik.
 * 
 * @author jjarolim
 */

import { LitElement } from 'lit'
import type { ReactiveController, ReactiveControllerHost } from 'lit'

export class ScrollTopController implements ReactiveController {
    private _host: ReactiveControllerHost
    private _observer: IntersectionObserver | null = null
    private _isScrolledTop: boolean = true
    private _callback: ((isScrolledTop: boolean) => void) | null = null
    private _isObserving: boolean = false

    constructor(
        host: ReactiveControllerHost,
        callback?: (isScrolledTop: boolean) => void
    ) {
        this._host = host
        this._callback = callback ?? null
        host.addController(this)
    }

    get isScrolledTop(): boolean {
        return this._isScrolledTop
    }

    hostConnected(): void {
        this._setupObserver()
        // Versuche sofort das Sentinel-Element zu beobachten
        // Falls es noch nicht existiert, wird es in hostUpdated() beobachtet
        this._observeSentinel()
    }

    hostDisconnected(): void {
        if (this._observer) {
            this._observer.disconnect()
            this._observer = null
            this._isObserving = false
        }
    }

    hostUpdate(): void {
        // Wird vor jedem Update aufgerufen
    }

    hostUpdated(): void {
        // Wird nach jedem Update aufgerufen
        // Hier können wir das Sentinel-Element beobachten, falls es noch nicht beobachtet wird
        if (!this._isObserving) {
            this._observeSentinel()
        }
    }

    private _observeSentinel(): void {
        if (!this._observer || this._isObserving) return
        
        const host = this._host as LitElement
        const sentinel = host.renderRoot?.querySelector('#sentinel')
        if (sentinel) {
            this._observer.observe(sentinel)
            this._isObserving = true
        }
    }

    private _setupObserver(): void {
        this._observer = new IntersectionObserver(
            ([entry]) => {
                const newValue = entry.isIntersecting
                if (this._isScrolledTop !== newValue) {
                    this._isScrolledTop = newValue
                    if (this._callback) {
                        this._callback(newValue)
                    }
                    // Trigger update im Host
                    this._host.requestUpdate()
                }
            },
            {
                threshold: [1.0]
            }
        )
    }
}
